/**
 * pdfThumbnailUtils.ts
 *
 * Shared helpers for rendering PDF page thumbnails via pdf.js.
 * Used by RotatePDFUploader, OrganizePDFUploader, RemovePagesPDFUploader, and SplitPDFUploader.
 */

/** Default scale for rendering thumbnail images */
export const PDF_THUMB_SCALE = 0.4;

/**
 * The CDN path for the pdf.js web worker.
 * Must match the pdf.js version loaded via <Script> in each page's uploader.
 */
const PDFJS_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';

/**
 * Ensures the pdf.js web worker is initialised.
 * Safe to call multiple times — only sets the URL once.
 */
export function ensurePdfWorker(pdfjsLib: any): void {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  }
}

/**
 * Returns the window-level pdfjsLib, or null if not yet loaded.
 */
export function getPdfjsLib(): any | null {
  if (typeof window === 'undefined') return null;
  return (window as any).pdfjsLib ?? null;
}

export interface ThumbnailRenderResult {
  /** Object URL pointing to a JPEG blob — caller must revoke when done */
  url: string;
  /** Rendered pixel width (= viewport.width) */
  width: number;
  /** Rendered pixel height (= viewport.height) */
  height: number;
  /**
   * The page's embedded /Rotate value from PDF metadata (0 | 90 | 180 | 270).
   * When `options.rotation` is not provided, this is applied automatically by pdf.js.
   */
  intrinsicRotation: number;
}

/**
 * Renders one PDF page to a JPEG blob URL.
 *
 * Rotation behaviour:
 *  - When `options.rotation` is **omitted**, pdf.js uses the page's intrinsic /Rotate value
 *    (the same default used by thumbnail grids on initial load).
 *  - When `options.rotation` is **provided**, that value is passed directly to getViewport,
 *    *replacing* the intrinsic rotation.  Pass `intrinsicRotation + userRotation` to combine both.
 *
 * @param pdf        - A pdf.js PDFDocumentProxy (from `pdfjsLib.getDocument().promise`).
 * @param pageIndex  - 0-based page index.
 * @param options    - Optional `rotation` (degrees) and `scale` overrides.
 * @returns          - Render result, or `null` on failure.
 */
export async function renderPageThumbnail(
  pdf: any,
  pageIndex: number,
  options?: { rotation?: number; scale?: number }
): Promise<ThumbnailRenderResult | null> {
  try {
    const scale = options?.scale ?? PDF_THUMB_SCALE;
    const page = await pdf.getPage(pageIndex + 1);
    const intrinsicRotation = (page.rotate as number) ?? 0;

    // Only pass rotation to getViewport when explicitly provided.
    // Omitting it lets pdf.js fall back to the page's /Rotate value automatically.
    const vpOptions =
      options?.rotation !== undefined ? { scale, rotation: options.rotation } : { scale };
    const viewport = page.getViewport(vpOptions);

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: context, viewport }).promise;

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.8)
    );
    if (!blob) return null;

    return {
      url: URL.createObjectURL(blob),
      width: viewport.width,
      height: viewport.height,
      intrinsicRotation,
    };
  } catch (err) {
    console.error('PDF thumbnail render error:', err);
    return null;
  }
}
